import { deleteScheduleFileAPI, getInterviewSpaceIdAPI, getInterviewSpaceSchedulesAPI, getScheduleFilesAPI, getSpaceUsersAPI, uploadLocalFileAPI, uploadYunFileAPI, getInterviewMeetingAPI } from '@/services/interview'
import { getMeetingAiRecordsAPI } from '@/services/meeting'
import { v4 as uuidv4 } from 'uuid'
import Vue from 'vue'

export default {
  namespaced: true,
  state: {
    scheduleId: '',
    spaceInfo: {}, // { spaceId, interviewTitle, chatId }
    interviewScheduleList: [], // { schedule_id, summary, round_name, candidate, files: [] }
    interviewUserList: [], // { schedule_id: '', ctime:'', user_info: [] }
    selectedContent: {}, // { fileName, fileNum, fileUrl, fileType, scheduleId }
    isFetchingSpaceInfo: false,
    fetchStatus: {
      code: -1,
      message: '',
      isError: false,
    },
    isResizing: false,
    showContentPanel: false,
    showSidebar: true,
    maximizedContentPanel: false,
    nextInterviewInfo: {}, // { link_url, start_time, end_time, code, tips }
    startAutoAIQa: false, // 是否开始自动AI问答
    isFinishedCurrentSchedule: false, // 是否完成当前日程的面试
    intervalId: null, // 轮询定时器ID
    showDebuggerInfo: false,
    lingxiTaskKey: new Date().getTime(), // 灵犀任务key
  },
  getters: {
    currentInterviewSchedule(state) {
      return state.interviewScheduleList.find((schedule) => schedule.scheduleId === state.scheduleId) || {}
    }
  },
  actions: {
    async getInterviewSpaceId({ state, commit }) {
      try {
        const spaceInfo = await getInterviewSpaceIdAPI(state.scheduleId)
        if (!Object.keys(spaceInfo).length) {
          throw new Error('获取面试空间ID失败')
        }
        commit('setSpaceInfo', {
          spaceId: spaceInfo.space_id,
          interviewTitle: spaceInfo.title,
          chatId: spaceInfo.chat_id,
        })
      } catch (error) {
        const errorMessage = error?.response?.data?.code === 10016 ? '面试日程不存在或已被删除' : '获取面试空间信息失败'
        Vue.prototype.$message.error(errorMessage)
        console.error('获取面试空间信息失败：', error?.response?.data?.message)
        commit('setFetchStatus', {
          code: error?.response?.data?.code || -1,
          message: error?.response?.data?.message || errorMessage,
          isError: true,
        })
      }
    },

    async getSheduleFiles({ dispatch }, scheduleId) {
      try {
        const files = await getScheduleFilesAPI(scheduleId)
        let isNoteExsit = false // 判断是否包含面试小记
        const filesList = (files?.list || []).map((file) => {
          const { file_name, file_url, file_type, file_num } = file

          if (file_type === 'note') {
            isNoteExsit = true
          }

          return {
            fileName: file_name,
            fileUrl: file_url,
            fileType: file_type,
            fileNum: file_num,
            scheduleId,
          }
        })

        // 如果没有面试小记，则添加一个空的面试小记
        if (!isNoteExsit) {
          filesList.push({
            fileName: '面试小记.otl',
            fileUrl: '',
            fileType: 'note',
            fileNum: uuidv4(),
            scheduleId,
            isPlaceholder: true,
          })
        }

        return filesList
      } catch (error) {
        console.error(`获取日程 ${scheduleId} 文件失败`, error)
        return []
      }
    },

    async getInterviewInterviewSpaceSchedules({ state, commit, dispatch }) {
      let hasFailedScheduleFetch = false
      try {
        const schedules = await getInterviewSpaceSchedulesAPI(state.spaceInfo.spaceId)
        if (!schedules?.schedule_infos || !schedules.schedule_infos?.length) {
          throw new Error('获取面试日程失败')
        }
        
        const schedulePromises = schedules.schedule_infos.map(async (schedule) => {
          const scheduleId = schedule.schedule_id
          const permission = schedule.permission ?? true // 面试日程资料权限，默认值为true
          const viewSummary = schedule.view_summary ?? true // 面试综合评价权限，默认值为true
          const originalFiles = await dispatch('getSheduleFiles', scheduleId)
          // console.log('originalFiles', originalFiles)
          const files = (!permission && viewSummary) ? originalFiles.filter((file) => file.fileType === 'assessment') : originalFiles
          const { summary, round_name, candidate, ctime } = schedule
          return {
            scheduleId,
            summary,
            roundName: round_name,
            candidate: candidate,
            type: 'kdocs',
            expanded: false,
            files,
            permission,
            viewSummary,
            ctime,
          }
        })

        const scheduleResults = await Promise.allSettled(schedulePromises)
        let scheduleList = []

        for (const result of scheduleResults) {
          if (result.status === 'rejected') {
            hasFailedScheduleFetch = true
          } else {
            const schedule = result.value
            const scheduleFiles = schedule.files || []
            scheduleFiles.forEach((file) => {
              if (file.fileType === 'assessment') {
                // 没有后缀名的文件，添加后缀名
                if (file?.fileName?.indexOf('assessment') === -1) {
                  file.fileName += '.assessment'
                }
                file.fileName = (schedule?.roundName ? schedule.roundName + '-' : '') + (schedule?.candidate ? schedule.candidate + '-' : '') + file.fileName
              }
            })
            scheduleList.push(schedule)
          }
        }
        scheduleList = scheduleList.map((item) => ({ ...item, title: item?.summary || '面试日程' }))
        // 展开当前日程
        if (state.scheduleId) {
          const currentSchedule = scheduleList.find((schedule) => schedule.scheduleId === state.scheduleId)
          if (currentSchedule) {
            currentSchedule.expanded = true
          }
        }
        commit('setInterviewScheduleList', scheduleList)
        _weblog.log('获取面试日程文件列表成功')
      } catch (error) {
        Vue.prototype.$message.error(error.message)
      }

      if (hasFailedScheduleFetch) {
        Vue.prototype.$message.error('获取面试日程失败')
      }
    },

    async getNextInterviewInfoBySpaceId({ commit }, spaceId) {
      try {
        const nextInterviewInfo = await getInterviewMeetingAPI(spaceId)
        commit('setNextInterviewInfo', nextInterviewInfo)
      } catch (error) {
        // Vue.prototype.$message.error('获取下一场面试信息失败', error)
        console.error('获取下一场面试信息失败', error)
      }
    },

    async getInterviewSpaceInfo({ state, commit, dispatch }) {
      // 清空面试详情数据
      await commit('setSpaceInfo', {})
      await commit('setInterviewScheduleList', [])
      await commit('setSelectedContent', {})
      await commit('setShowContentPanel', false)
      await commit('setMaximizedContentPanel', false)
      await commit('setShowSidebar', true)
      await commit('setNextInterviewInfo', {})
      await commit('setFetchStatus', { code: -1, message: '', isError: false }) // 重置 error 状态
      // await commit('setInterviewUserList', [])
      commit('setIsFetchingSpaceInfo', true)
      commit('setStartAutoAIQa', false) // 重置自动问答状态
      commit('setIsFinishedCurrentSchedule', false) // 重置当前日程完成状态
      // 重置轮询定时器ID
      if (state.intervalId) {
        clearInterval(state.intervalId)
        await commit('setIntervalId', null)
      }
      
      // 获取最新面试详情数据
      await dispatch('getInterviewSpaceId')
      if (state.fetchStatus.isError) {
        commit('setIsFetchingSpaceInfo', false)
        return
      }
      await dispatch('getInterviewInterviewSpaceSchedules')

      // 刷新当前日程的文件列表，直到获取到JD和Resume文件或者轮询次数达到上限
      const maxRetries = 30
      const interval = 3000 // 3 seconds

      const checkRequiredFilesExist = await new Promise((resolve) => {
        let retries = maxRetries
        let shouldPoll = true
        let newFiles = []

        const checkFunc = async () => {
          const scheduleIdx = state.interviewScheduleList.findIndex((schedule) => schedule.scheduleId === state.scheduleId)
          if (scheduleIdx === -1) {
            return resolve(false)
          }
          const schedule = state.interviewScheduleList[scheduleIdx]
          const hasResume = schedule.files.some((file) => file.fileType === 'resume')
          const hasJD = schedule.files.some((file) => file.fileType === 'jd')
          const hasTranscription = schedule.files.some((file) => file.fileType === 'transcription')
          const hasSummary = schedule.files.some((file) => file.fileType === 'assessment')
          const originalFiles = hasSummary ? [schedule.files[0]] : []

          // console.log(state.scheduleId, '轮询中...', hasJD, hasResume, hasTranscription, hasSummary, (maxRetries - retries + 1), schedule.files.length, schedule.files)
          _weblog.log(`轮询中，scheduleId: ${state.scheduleId}, hasJD: ${hasJD}, hasResume: ${hasResume}, hasTranscription: ${hasTranscription}, hasSummary: ${hasSummary}`)
          _weblog.log(`(maxRetries - retries + 1): ${maxRetries - retries + 1}, schedule.files.length: ${schedule.files.length}`)

          if (hasResume && !state.selectedContent?.fileNum) {
            // 只有不是占位符的文件才会被选中
            const material = schedule.files.find((file) => file.fileType === 'resume' && !file.isPlaceholder)
            commit('setSelectedContent', { ...material })
            commit('setShowContentPanel', true)
          }
          
          const condition = hasSummary ? (hasJD && hasResume && hasTranscription) : (hasJD && hasResume)
          if (condition || retries <= 0) {
            shouldPoll = false // 停止轮询
            clearInterval(state.intervalId)
            commit('setIntervalId', null)
            if (hasTranscription) {
              commit('setIsFinishedCurrentSchedule', true)
            }
            _weblog.log('轮询结束，触发灵犀问答：condition:', condition, 'retries:', retries)
            return resolve(condition)
          } else {
            newFiles = await dispatch('getSheduleFiles', state.scheduleId)
          }
          
          Vue.set(state.interviewScheduleList, scheduleIdx, {
            ...schedule,
            files: [...originalFiles, ...newFiles],
          })
          retries -= 1
        }

        checkFunc() // 立即执行一次检查
        if (shouldPoll) {
          const intervalId = setInterval(checkFunc, interval) // 设置定时器
          commit('setIntervalId', intervalId)
        }
      })
      
      // 初始选择的内容信息为当前日程中的简历 或者 第一个文件
      const currentSchedule = state.scheduleId ? state.interviewScheduleList.find((schedule) => schedule.scheduleId === state.scheduleId) : state.interviewScheduleList[0]
      if (currentSchedule && currentSchedule.files.length > 0) {
        const material = currentSchedule.files.find((file) => file.fileType === 'resume') || currentSchedule.files[0]
        // 用户已经选择了文件，则不再自动选择
        if (!material?.isPlaceholder && !state.selectedContent?.fileNum) {
          // 只有不是占位符的文件才会被选中
          commit('setSelectedContent', { ...material })
          commit('setShowContentPanel', true)
        }
      }
      
      commit('setIsFetchingSpaceInfo', false)
      // 文件准备完成，自动开始AI问答
      if (!checkRequiredFilesExist) {
        console.error('简历、岗位说明（及语音转写）文件未准备完成，自动问答未开始')
        Vue.prototype.$message.error('简历、岗位说明（及语音转写）文件未拉取成功，自动问答未开始')
      }
      await commit('setStartAutoAIQa', checkRequiredFilesExist)
      _weblog.log('轮询结束，checkRequiredFilesExist:', checkRequiredFilesExist)
      if (checkRequiredFilesExist) {
        commit('setLingxiTaskKey')
      }
      // 获取下一场面试信息
      await dispatch('getNextInterviewInfoBySpaceId', state.spaceInfo.spaceId)
    },

    async getInterviewSpaceUsers({ state, commit }) {
      try {
        const usersInfo = await getSpaceUsersAPI(state.spaceInfo.spaceId)
        commit('setInterviewUserList', usersInfo?.list || [])
      } catch (error) {
        Vue.prototype.$message.error('获取面试相关用户失败', error)
      }
    },

    async uploadScheduleFile({ commit }, fileInfo, source = 'yun') {
      try {
        let fileData = {}
        if (source === 'local') {
          fileData = await uploadLocalFileAPI(fileInfo)
        } else if (source === 'yun') {
          fileData = await uploadYunFileAPI(fileInfo)
        } else {
          throw new Error('未知的文件来源')
        }

        // 上一步操作成功后，才能更新状态
        const newFile = {
          scheduleId: fileInfo.scheduleId,
          fileName: fileInfo.fileName,
          fileType: fileInfo.fileType,
          fileUrl: fileData?.link_url, // 上传成功后的文件地址
          fileNum: fileData?.file_num, // 上传成功后的文件唯一标识
        }
        commit('pushScheduleFile', { scheduleId: fileInfo.scheduleId, file: newFile, isHeadPush: true })

        // 添加文件成功后，自动添加到AI问答列表中
        const curFile = {
          id: newFile.fileUrl.split('/').pop(),
          name: newFile.fileName,
          size: newFile?.fileSize || fileInfo?.fileSize || 0,
        }
        Vue.prototype.$EventBus.$emit('fillLingxiInputbox', {
          fileList: [curFile],
        })
      } catch (error) {
        Vue.prototype.$message.error('上传失败', error)
        return ''
      }
    },

    async deleteScheduleFile({ state, commit }, { scheduleId, fileList }) {
      try {
        await deleteScheduleFileAPI(
          scheduleId,
          fileList.map((file) => ({ file_num: file.fileNum }))
        )
        commit('removeScheduleFile', { scheduleId, fileList }) // 上一步操作成功后，才能更新状态

        // 删除的文件是当前选中的文件，清空选中的文件信息
        if (fileList.some((file) => file.fileNum === state.selectedContent.fileNum)) {
          commit('setSelectedContent', {})
          commit('setShowContentPanel', false)
        }
      } catch (error) {
        Vue.prototype.$message.error('删除失败', error)
      }
    },
  },
  mutations: {
    setIsResizing(state, isResizing) {
      state.isResizing = isResizing
    },
    setScheduleId(state, scheduleId) {
      state.scheduleId = scheduleId
    },
    setSpaceInfo(state, spaceInfo) {
      state.spaceInfo = spaceInfo
    },
    setNextInterviewInfo(state, nextInterviewInfo) {
      state.nextInterviewInfo = nextInterviewInfo
    },
    setSelectedContent(state, content) {
      state.selectedContent = content
    },
    setInterviewScheduleList(state, scheduleList) {
      state.interviewScheduleList = scheduleList
    },
    setInterviewUserList(state, userList) {
      state.interviewUserList = userList
    },
    setShowContentPanel(state, showContentPanel) {
      state.showContentPanel = showContentPanel
    },
    setShowSidebar(state, showSidebar) {
      state.showSidebar = showSidebar
    },
    setMaximizedContentPanel(state, maximizedContentPanel) {
      state.maximizedContentPanel = maximizedContentPanel
    },
    setIsFetchingSpaceInfo(state, isFetchingSpaceInfo) {
      state.isFetchingSpaceInfo = isFetchingSpaceInfo
    },
    setFetchStatus(state, fetchStatus) {
      state.fetchStatus = fetchStatus
    },
    setStartAutoAIQa(state, startAutoAIQa) {
      state.startAutoAIQa = startAutoAIQa
    },
    setIsFinishedCurrentSchedule(state, isFinishedCurrentSchedule) {
      state.isFinishedCurrentSchedule = isFinishedCurrentSchedule
    },
    setIntervalId(state, intervalId) {
      state.intervalId = intervalId
    },

    // 一次支持同时删除多个文件
    removeScheduleFile(state, { scheduleId, fileList }) {
      const schedule = state.interviewScheduleList.find((schedule) => schedule.scheduleId === scheduleId)
      if (schedule) {
        const needToDeleteFiles = fileList.map((file) => file.fileNum)
        const newFiles = schedule.files.filter((file) => !needToDeleteFiles.includes(file.fileNum))
        schedule.files = newFiles
      }
    },

    // 一次只支持追加一个文件
    pushScheduleFile(state, { scheduleId, file, isHeadPush = false }) {
      const schedule = state.interviewScheduleList.find((schedule) => schedule.scheduleId === scheduleId)
      if (schedule) {
        const method = isHeadPush ? 'unshift' : 'push'
        schedule.files[method](file)
      }
    },

    // 更新文件信息
    updateScheduleFile(state, file) {
      const schedule = state.interviewScheduleList.find((schedule) => schedule.scheduleId === file.scheduleId)
      if (schedule) {
        const index = schedule.files.findIndex((f) => f.fileNum === file.fileNum)
        if (index !== -1) {
          Vue.set(schedule.files, index, file) // 使用 Vue.set 更新数组中的对象
        }
      }
    },

    setShowDebuggerInfo(state, showDebuggerInfo) {
      state.showDebuggerInfo = showDebuggerInfo
    },
    setLingxiTaskKey(state) {
      state.lingxiTaskKey = new Date().getTime()
    },
  },
}
